import React from 'react';

/**
 * 🚪🖼️ RoomPicker (09-18; build contract = mockup v12, Nina's 09-17 rulings).
 *
 * TWO doors over ONE model (utils/roomView roomPickerRows / roomPickerCards):
 *   · RoomPickerList  — the rows under a committed "/room " in the composer
 *     palette (where the chips used to be). Tap a row = GO (send, not fill).
 *   · RoomPickerCards — the pictures the DOOR (RoomDoorButton, in the
 *     message box) / a bare "/room" opens over
 *     the chat. Whole 21:9 image, caption BENEATH, 3 columns, main room
 *     double-wide first.
 *
 * Pure presentation: every string arrives ready, every action is a callback.
 * ⭐⭐ Nothing is drawn over a picture. The ONE ruled exception is the plain
 *   📍 "you" pin (emoji + white word, centered, never a chip).
 * ⭐⭐ No locked/undiscovered rooms — the model never sends one.
 * ⭐ Marks are TEXT tags. No fog emoji (no glyph on Nina's Mac).
 * ⭐ This frontend has NO global box-sizing: every padded/bordered box that
 *   takes a width sets border-box itself.
 */
const LINE = 'rgba(122,96,60,0.55)';
const GOLD = '#e9b565';
const YOU = '#ffd98a';
const INVITE = '#8e7ac4';
// 👥 10-01 people: amber glow strengths (never purple: the invite owns purple)
const HEAT = 'rgba(233,181,101,';
const HEAT_GLOW = [null,
  `0 0 0 1px ${HEAT}0.35), 0 0 8px ${HEAT}0.25)`,
  `0 0 0 1px ${HEAT}0.55), 0 0 14px ${HEAT}0.40)`,
  `0 0 0 1px ${HEAT}0.80), 0 0 20px ${HEAT}0.55)`];
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

// The art slot. `image` = a room's real painting (or the banner for the main
// room); `treated` = the banner under the room's own grade (a room with no
// painting yet); `layers` = the outdoor takeover sky; null = a plain panel.
export function PickerArt({ art, fit = 'cover' }) {
  if (!art) return <div style={{ position: 'absolute', inset: 0, background: '#2c2117' }} />;
  if (art.kind === 'layers') {
    return art.layers.map((bg, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <div key={i} style={{ position: 'absolute', inset: 0, background: bg }} />
    ));
  }
  return (
    <img
      src={art.url}
      alt=""
      loading="lazy"
      draggable={false}
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        objectFit: fit, objectPosition: 'center', display: 'block',
        filter: art.kind === 'treated' ? art.filter : undefined,
      }}
      // a dead url leaves the panel colour, never a broken-image glyph
      onError={(e) => { e.target.style.visibility = 'hidden'; }}
    />
  );
}

function Faces({ faces, size = 20, max = 5 }) {
  if (!faces || faces.length === 0) return null;
  const shown = faces.slice(0, max);
  const extra = faces.length - shown.length;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
      {shown.map((f) => (f.avatar ? (
        <img key={f.name} src={f.avatar} alt={f.name} title={f.name} draggable={false}
          style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', background: '#000', border: `1.5px solid ${f.person ? '#e6dac6' : 'rgba(255,255,255,0.13)'}` /* 👥 10-01 people: a cream ring = a person */, boxSizing: 'border-box' }}
          onError={(e) => { e.target.style.visibility = 'hidden'; }} />
      ) : (
        <span key={f.name} title={f.name} aria-label={f.name}
          style={{ width: size, height: size, borderRadius: '50%', background: '#3a2c1e', color: '#e6dac6', fontSize: size * 0.5, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: `1.5px solid ${f.person ? '#e6dac6' : 'rgba(255,255,255,0.13)'}` /* 👥 10-01 people: a cream ring = a person */, boxSizing: 'border-box' }}>
          {f.name.charAt(0).toUpperCase()}
        </span>
      )))}
      {extra > 0 && <span style={{ fontFamily: MONO, fontSize: 10.5, color: '#d9ccb6' }}>+{extra}</span>}
    </span>
  );
}

const markStyle = {
  fontFamily: MONO, fontSize: 9.5, letterSpacing: '.06em', textTransform: 'uppercase',
  color: '#a8977f', background: '#2c2117', border: `1px solid ${LINE}`, borderRadius: 5,
  padding: '0 5px', whiteSpace: 'nowrap', flexShrink: 0,
};

/** 🚪▾ The DOOR (Nina 09-18, mockup option B: "the arrow lives in the message
 *  box"): one small button at the left of the message box, the SAME place in
 *  every room and on phones — the room you're in + a ▾. Opens/closes the
 *  pictures. Fixed width on purpose: the composer reserves exactly DOOR_W for
 *  it on the wrapped phone row (the textarea claims the rest), so it can never
 *  be pushed onto a line of its own. mousedown is swallowed so the textarea
 *  keeps focus (a blur collapses the phone dock — the 08-02 scroll-up RCA). */
export const DOOR_W = 46;
export function RoomDoorButton({ icon, label, open = false, onToggle }) {
  return (
    <button type="button" data-room-door="1"
      aria-label={`Rooms. You are in ${label || 'the main room'}`} aria-haspopup="dialog" aria-expanded={open}
      title="Rooms"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => { if (onToggle) onToggle(); }}
      style={{
        flex: `0 0 ${DOOR_W}px`, width: DOOR_W, minHeight: 40, boxSizing: 'border-box', padding: 0,
        // stacked (icon over ▾): the desktop row is ~90px tall (it stretches to
        // the Send column) and the phone row ~40px — a column reads at both.
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
        borderRadius: 4, cursor: 'pointer', font: 'inherit', fontSize: 17, lineHeight: 1,
        background: open ? 'rgba(233,181,101,0.16)' : 'rgba(0,0,0,0.45)',
        border: `1px solid ${open ? GOLD : LINE}`, color: GOLD,
      }}>
      <span aria-hidden="true">{icon || '🕯️'}</span>
      <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 0.8, transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
    </button>
  );
}

/** The LIST (composer palette). onGo(id) fires on mousedown so the textarea
 *  never loses focus first (same pattern as every other palette chip). */
export function RoomPickerList({ rows, onGo, compact = false }) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return (
    <div data-room-picker="list" role="listbox" aria-label="Rooms"
      style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: compact ? '38dvh' : '46vh', overflowY: 'auto', minWidth: 0 }}>
      {rows.map((r) => (
        <button key={r.id} type="button" role="option" aria-selected={r.you} disabled={r.you}
          onMouseDown={(e) => { e.preventDefault(); if (!r.you && onGo) onGo(r.id); }}
          style={{
            display: 'grid', gridTemplateColumns: `${compact ? 56 : 64}px minmax(0,1fr) auto 14px`, gap: 10, alignItems: 'center',
            width: '100%', boxSizing: 'border-box', padding: '4px 8px 4px 4px', borderRadius: 8, textAlign: 'left',
            font: 'inherit', color: '#efe4d2', cursor: r.you ? 'default' : 'pointer',
            border: `1px solid ${r.you ? YOU : (r.glow ? INVITE : 'transparent')}`,
            background: r.you ? 'rgba(255,217,138,0.06)' : (r.glow ? 'rgba(142,122,196,0.10)' : 'transparent'),
          }}>
          <span style={{ position: 'relative', display: 'block', width: '100%', aspectRatio: '16 / 9', borderRadius: 6, overflow: 'hidden', border: `1px solid ${LINE}`, boxSizing: 'border-box', background: '#2c2117' }}>
            <PickerArt art={r.art} />
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span style={{ fontWeight: 800, fontSize: 13, color: r.you ? YOU : '#efe4d2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                {r.glow ? '🎪 ' : `${r.icon} `}{r.label}
              </span>
              {r.mark && <span style={markStyle}>{r.mark}</span>}
            </span>
            <span style={{ fontSize: 11.5, color: '#a8977f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {r.you ? "you're here" : r.sub}
            </span>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            {!compact && <Faces faces={r.faces} size={20} max={4} />}
            {r.people > 0
              ? <span style={{ fontFamily: MONO, fontSize: 10.5, color: '#d9ccb6', whiteSpace: 'nowrap' }}>{r.people === 1 ? '1 person' : `${r.people} people`}</span>
              : (r.watching > 0 && <span style={{ fontFamily: MONO, fontSize: 10.5, color: '#d9ccb6', whiteSpace: 'nowrap' }}>{r.watching} watching</span>)}{/* 👥 10-01 people */}
          </span>
          <span aria-hidden="true" style={{ color: '#a8977f', fontWeight: 900 }}>{r.you ? '' : '›'}</span>
        </button>
      ))}
    </div>
  );
}

/** The PICTURES. columns = 3 (desktop) / 2 (phones); the main room spans two.
 *  The caller positions the shell (absolute over the feed on desktop, in-flow
 *  at the top of the phone dock) and owns Esc / outside / close-on-move. */
// 👁 onPeek (09-18 late, ROOM_VERBS_PEEK_SPEC D2): optional. When given, every
// card but your own gets an eye in its CAPTION ROW (never on the picture) that
// looks into that room without going there. Omitted = no eye, no change.
// ⚠️ The eye is a real <button>, so it can NOT live inside the card's own
// <button> (invalid HTML, and Firefox never dispatches clicks to a button's
// children): the card is a wrapper, the PICTURE is the go-button.
export function RoomPickerCards({ cards, onGo, onClose, onPeek = null, columns = 3 }) {
  if (!Array.isArray(cards) || cards.length === 0) return null;
  return (
    <div data-room-picker="cards" role="dialog" aria-label="Rooms"
      style={{
        background: '#231a12', border: `1px solid ${LINE}`, borderRadius: 10, padding: 10,
        boxSizing: 'border-box', maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: 8,
        boxShadow: '0 -10px 40px rgba(0,0,0,0.6)',
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#a8977f' }}>
        <b style={{ color: GOLD, fontWeight: 900, fontSize: 13 }}>🚪 Rooms</b>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          tap a room to go
          <button type="button" aria-label="close" onClick={onClose}
            style={{ font: 'inherit', fontSize: 12, padding: '1px 7px', borderRadius: 6, background: '#2c2117', border: `1px solid ${LINE}`, color: '#efe4d2', cursor: 'pointer' }}>
            ✕
          </button>
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: columns === 3 ? 12 : 10 }}>
        {cards.map((c) => {
          const edge = c.you ? YOU : (c.glow ? INVITE : (c.heat > 0 ? `${HEAT}0.6)` : LINE)); // 👥 10-01 people
          const go = () => { if (!c.you && onGo) onGo(c.id); };
          const wide = c.span === 2 && columns === 3;
          return (
            <div key={c.id} data-room-card={c.id} data-testid={`room-card-${c.id}`}
              style={{
                gridColumn: c.span === 2 ? 'span 2' : undefined,
                display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0,
              }}>
              <button type="button" disabled={c.you}
                onClick={go}
                title={c.you ? "You're here" : `Go to ${c.name}`}
                style={{
                  display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%', boxSizing: 'border-box',
                  // the double-wide main card fills its grid cell so the row stays one height
                  ...(wide ? { flex: '1 1 auto', minHeight: 0 } : {}),
                  background: 'none', border: 0, padding: 0, textAlign: 'left', font: 'inherit',
                  color: '#efe4d2', cursor: c.you ? 'default' : 'pointer',
                }}>
                {/* The picture: a 21:9 box for every room (the art was painted at
                    21:9, so cover == the WHOLE image). The double-wide main card
                    fills its grid cell instead, so the row stays one height. */}
                <span data-testid={`room-pic-${c.id}`} style={{
                  position: 'relative', display: 'block', width: '100%', boxSizing: 'border-box',
                  ...(wide ? { flex: '1 1 auto', minHeight: 0 } : { aspectRatio: '21 / 9' }),
                  borderRadius: 9, overflow: 'hidden', background: '#2c2117',
                  border: `1px solid ${edge}`,
                  boxShadow: c.you ? `0 0 0 1px ${YOU}` : (c.glow ? `0 0 0 1px ${INVITE}, 0 0 18px rgba(142,122,196,0.5)` : (c.heat > 0 ? HEAT_GLOW[c.heat] : 'none')), // 👥 10-01 people
                }}>
                  <PickerArt art={c.art} />
                  {c.you && (
                    // the ONE mark allowed on a picture (ruled 09-17): plain, never a chip
                    <span aria-hidden="true" style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, pointerEvents: 'none' }}>
                      <span style={{ fontSize: 30, lineHeight: 1, filter: 'drop-shadow(0 3px 5px rgba(0,0,0,0.85))' }}>📍</span>
                      <span style={{ fontWeight: 900, fontSize: 14, color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.95), 0 0 3px #000', letterSpacing: '.02em' }}>you</span>
                    </span>
                  )}
                </span>
              </button>
              {/* the caption sits BENEATH the picture, never on it. The name still
                  goes on a mouse tap (the whole card used to be one button); the
                  keyboard path is the picture button above. */}
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 22, padding: '0 2px', minWidth: 0 }}>
                {/* overflow hidden: a non-shrinking outdoor mark used to spill over the faces */}
                <span onClick={go} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0, overflow: 'hidden', cursor: c.you ? 'default' : 'pointer' }}>
                  <span style={{ fontWeight: 800, fontSize: 13, color: c.you ? YOU : '#efe4d2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                    {c.glow ? '🎪 ' : ''}{c.name}
                  </span>
                  {c.mark && <span style={markStyle}>{c.mark}</span>}
                </span>
                <span style={{ flex: 1 }} />
                {/* 09-19 audit: with the eye in the row, a 2-column phone card
                    (~152px) left ~12px for the NAME on a busy card, and the
                    busiest card is the one people look for. The eye costs a
                    face: 2 on phones when it is shown. */}
                <Faces faces={c.faces} size={20} max={columns === 3 ? 5 : ((onPeek && !c.you) ? 2 : 3)} />
                {onPeek && !c.you && (
                  <button type="button" aria-label={`Peek into ${c.name}`} title={`Peek into ${c.name}`}
                    // keep the message box focused (a blur collapses the phone dock)
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => { e.stopPropagation(); onPeek(c.id); }}
                    style={{
                      // a real thumb target (was ~29x19, 5px under the go-button)
                      flex: '0 0 auto', font: 'inherit', fontSize: 12, lineHeight: 1, padding: '6px 9px', minHeight: 30, borderRadius: 999,
                      cursor: 'pointer', background: '#2c2117', border: `1px solid ${LINE}`, color: '#efe4d2', boxSizing: 'border-box',
                    }}>
                    peek
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
